import { useState, useEffect, useCallback, useRef } from 'react';
import { fetch } from '../../utils/api-wrapper';
import { validateExpenseReviewForm } from './utils/validate-form.js';
import { cleanConditionalFields } from './utils/conditional-fields.js';
import {
  buildExpensePayload,
  expenseJsonToReviewModel,
  reviewMainInfoFromExpenseData,
} from './utils/contract-expense-json.js';
import {
  EMPTY_RULE_ALERTS,
  evaluateExpenseReviewRuleAlerts,
  isReviewExpenseEvaluableForRules,
} from './utils/evaluate-expense-rules.js';

export const DEFAULT_REVIEW_EXPENSE = {
  matter: '',
  date: '',
  category: '',
  activity: null,
  costType: '',
  imputationLevel1Id: null,
  imputationLeafId: null,
  imputationVerifyCategory: null,
  isBillable: true,
  info: {
    narrative: '',
    amount: 0,
    numberOfGuests: 1,
    taxCode: null,
    tip: null,
    location: '',
    distance: 0,
  },
};

const RULE_EVAL_DEBOUNCE_MS = 400;

/**
 * Form state, live rule evaluation and pre-save checks of the expense review form.
 *
 * Shared by the review screen and the invoice editor's add-expense modal
 * (XPEN-3551), so both apply the same client configuration, conditional fields,
 * validator and rules. Each caller decides only where the payload goes.
 *
 * @param {object} args
 * @param {object} args.data - MobX data store (translations and client config).
 * @param {object|null} args.expenseData - Stored expense `{ json, ... }` the form edits.
 * @param {string} args.defaultTaxCode - Tax code used when the expense has none.
 */
export default function useExpenseReviewForm({ data, expenseData, defaultTaxCode }) {
  const [validationErrors, setValidationErrors] = useState([]);
  const [ruleAlerts, setRuleAlerts] = useState(EMPTY_RULE_ALERTS);
  const [isExpenseValid, setIsExpenseValid] = useState(false);
  const ruleEvalRequestIdRef = useRef(0);
  /**
   * Synced from ExpenseComponent: whether imputation leaf is required for submit validation.
   * Single-expense assumption: this form renders exactly one ExpenseComponent so one stage
   * value suffices. If multi-expense forms are ever built, refactor to { [expenseIndex]: stage }.
   */
  const [imputationLeafStage, setImputationLeafStage] = useState('loading');
  const imputationRowsRef = useRef({ level1Row: null, leafRow: null });
  const [mainInfo, setMainInfo] = useState({
    invoiceNumber: '',
    date: new Date().toISOString().slice(0, 10),
  });
  const [expense, setExpense] = useState(DEFAULT_REVIEW_EXPENSE);

  const handleExpenseValidationChange = useCallback((_expenseIndex, isValid) => {
    setIsExpenseValid(Boolean(isValid));
  }, []);

  const handleImputationLeafStageChange = useCallback((_expenseIndex, stage) => {
    setImputationLeafStage(stage);
  }, []);

  const handleImputationRowsChange = useCallback((rows) => {
    imputationRowsRef.current = rows ?? { level1Row: null, leafRow: null };
  }, []);

  useEffect(() => {
    if (!expenseData?.json) {
      return;
    }
    setMainInfo(reviewMainInfoFromExpenseData(expenseData));
    setExpense(expenseJsonToReviewModel(expenseData.json, defaultTaxCode));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reseed only when a new expense arrives; a tax-profile reload must not wipe the user's edits
  }, [expenseData]);

  const handleUpdateExpense = useCallback((info) => {
    setExpense((prevExpense) => {
      const merged = { ...prevExpense };
      for (const key in info) {
        if (Object.prototype.hasOwnProperty.call(info, key)) {
          const value = info[key];
          if (
            typeof value === 'object' &&
            value !== null &&
            !Array.isArray(value) &&
            prevExpense[key] &&
            typeof prevExpense[key] === 'object'
          ) {
            merged[key] = { ...prevExpense[key], ...value };
          } else {
            merged[key] = value;
          }
        }
      }
      return merged;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = validateExpenseReviewForm(data, mainInfo, [expense], {
      imputationLeafStages: [imputationLeafStage],
    });
    if (errors.length > 0) {
      setValidationErrors(errors);
      return false;
    }
    setValidationErrors([]);
    return true;
  }, [data, mainInfo, expense, imputationLeafStage]);

  const expenseReviewConfig = data.config;

  useEffect(() => {
    if (!expenseData?.json) {
      return;
    }

    const cleanedExpense = cleanConditionalFields(expense, data);
    if (!isReviewExpenseEvaluableForRules({ cleanedExpense, data, imputationLeafStage })) {
      setRuleAlerts(EMPTY_RULE_ALERTS);
      return;
    }

    const requestId = ++ruleEvalRequestIdRef.current;
    const timer = setTimeout(async () => {
      try {
        const alerts = await evaluateExpenseReviewRuleAlerts({
          fetchFn: fetch,
          expenseData,
          cleanedExpense,
          mainInfo,
          data,
          imputationRows: imputationRowsRef.current,
          readLabel: (key) => data.t.read(key),
        });
        if (ruleEvalRequestIdRef.current === requestId) {
          setRuleAlerts(alerts);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('live expense rule evaluation failed', error);
        }
      }
    }, RULE_EVAL_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [expense, mainInfo, expenseData, imputationLeafStage, expenseReviewConfig]);

  /**
   * Runs validation, conditional-field cleanup and rules; resolves to the payload
   * to send, or null when something must be fixed first (errors are already set).
   * Blocking rules stop the save; warnings and exceptions are shown but do not.
   */
  const prepareSubmission = useCallback(async () => {
    if (!validateForm()) {
      return null;
    }

    if (!expenseData?.json) {
      setValidationErrors([data.t.read('review_expense.error.update_failed')]);
      return null;
    }

    const cleanedExpense = cleanConditionalFields(expense, data);

    try {
      const alerts = await evaluateExpenseReviewRuleAlerts({
        fetchFn: fetch,
        expenseData,
        cleanedExpense,
        mainInfo,
        data,
        imputationRows: imputationRowsRef.current,
        readLabel: (key) => data.t.read(key),
      });
      setRuleAlerts(alerts);
      if (alerts.blocking.length > 0) {
        return null;
      }
    } catch (error) {
      console.warn('expense rule evaluation failed', error);
      setValidationErrors([data.t.read('validation_error')]);
      return null;
    }

    setRuleAlerts(EMPTY_RULE_ALERTS);
    return buildExpensePayload(
      cleanedExpense,
      expenseData,
      mainInfo,
      data,
      imputationRowsRef.current
    );
  }, [validateForm, expenseData, expense, mainInfo, data]);

  return {
    expense,
    handleUpdateExpense,
    mainInfo,
    setMainInfo,
    validationErrors,
    setValidationErrors,
    ruleAlerts,
    isExpenseValid,
    handleExpenseValidationChange,
    handleImputationLeafStageChange,
    handleImputationRowsChange,
    prepareSubmission,
  };
}
